import {
  ONE_MINUTE_OF_MS,
  FULL_DATE_TIME_ZONE,
  FULL_DATE_TIME,
} from "../../../../../utils/timeUtils";
import {
  useFormatZoned,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { TagDisplay } from "../TimelineTooltipTagDisplay";
import { useTimelineTooltipStore } from "../timelineTooltipStore";
import { useCopyToClipboard } from "react-use";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { SignalValidationDropdownAndChart } from "./SignalValidationDropdownAndChart";
import { useState } from "react";
import { MoreVert } from "@mui/icons-material";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { useDispatch } from "react-redux";
import { setFocusRange } from "providers/GlobalStateProvider";
import GoToRangeIcon from "@mui/icons-material/Expand";
import { useSearchParams } from "react-router-dom";
import { focusRangeDisplayAtom } from "components/Dashboard/range-selectors/CustomRangeSelector";
import { useAtom } from "jotai";

/** Main panel with basic info on the selected segment */
export function AboutPanel({
  openRemoveRetrotagForm,
  tagNameDisplay,
  machemetryId,
}: {
  openRemoveRetrotagForm: () => void;
  tagNameDisplay?: string;
  machemetryId: string | null;
}) {
  const formatZoned = useFormatZoned();

  const { translate } = useFlightTranslation();
  const isStorybook = useIsStorybook();
  const [{ selectedProdSegment, isModalPinned }] = useTimelineTooltipStore();
  const { ge, lt } = selectedProdSegment?.range ?? { ge: null, lt: null };

  const [copiedText, setCopiedText] = useCopyToClipboard();

  const [
    {
      general: { timelineTooltipEnableSignalValidation },
    },
  ] = useFlightConfig();

  if (!ge) {
    return null;
  }
  const truncLt = lt || Date.now();
  const duration = ((+truncLt - ge) / ONE_MINUTE_OF_MS).toFixed(0);
  const hours = Math.floor(+duration / 60).toFixed(0);
  const minutes = (+duration - +hours * 60).toFixed(0);

  return (
    <div className="about panel">
      {timelineTooltipEnableSignalValidation && isModalPinned && (
        <SignalValidationDropdownAndChart
          {...{
            ge,
            lt: truncLt,
            machemetryId,
          }}
        />
      )}
      <div className="textAlignCenter centeredGrid">
        <h6 className="sectionHeader">
          {translate("Duration")} <GoToThisRangeMenu />
        </h6>
        <p
          className="datetime"
          {...(timelineTooltipEnableSignalValidation
            ? {
                style: { cursor: "pointer" },
                onClick: () => {
                  setCopiedText(String(ge));
                },
              }
            : {})}
        >
          {ge &&
            (copiedText.value === String(ge)
              ? "Copied!"
              : formatZoned(
                  Number(ge),
                  isStorybook ? FULL_DATE_TIME : FULL_DATE_TIME_ZONE
                ))}
        </p>
        <p
          className="datetime"
          {...(timelineTooltipEnableSignalValidation
            ? {
                style: { cursor: "pointer" },
                onClick: () => {
                  setCopiedText(String(truncLt));
                },
              }
            : {})}
        >
          {truncLt &&
            (copiedText.value === String(truncLt)
              ? "Copied!"
              : formatZoned(
                  Number(truncLt),
                  isStorybook ? FULL_DATE_TIME : FULL_DATE_TIME_ZONE
                ))}
        </p>
        <p className="datetime">
          {` (~`}
          {hours !== "0" && `${hours} hr${hours === "1" ? "" : "s"} `}
          {`${minutes} min)`}
        </p>
      </div>
      {tagNameDisplay && (
        <TagDisplay
          {...{
            tagNameDisplay,
            openRemoveRetrotagForm,
            machemetryId,
          }}
        />
      )}
    </div>
  );
}

/** a three-dots menu that contains one option: "Go to this range" */
export function GoToThisRangeMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <div style={{ marginRight: -24 }}>
      <IconButton style={{ width: 24, height: 24 }} onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu
        id="menu-timeline-tooltip"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
      >
        <GoToThisRangeMenuItem {...{ handleClose }} />
      </Menu>
    </div>
  );
}

function GoToThisRangeMenuItem({ handleClose }) {
  const dispatch = useDispatch();
  const [{ selectedProdSegment }] = useTimelineTooltipStore();
  const [, setSearchParams] = useSearchParams();
  const [, setFocusRangeDisplay] = useAtom(focusRangeDisplayAtom);
  const workSchedule = useWorkScheduleIdOnCurrentPage();
  const handleClick = () => {
    if (selectedProdSegment) {
      dispatch(
        setFocusRange({
          ge: Number(selectedProdSegment.range.ge),
          lt: Number(selectedProdSegment.range.lt),
        })
      );
      // set url params ge, lt to match the selected range
      if (selectedProdSegment.range.ge && selectedProdSegment.range.lt) {
        setSearchParams({
          workSchedule,
          ge: String(selectedProdSegment.range.ge),
          lt: String(selectedProdSegment.range.lt),
        });
        // update focusRangeDisplay on the range selector
        setFocusRangeDisplay({
          ge: Number(selectedProdSegment.range.ge),
          lt: Number(selectedProdSegment.range.lt),
        });
      }
    }
    handleClose();
  };
  return (
    <MenuItem onClick={handleClick}>
      <GoToRangeIcon style={{ transform: "rotate(90deg)", marginRight: 6 }} />{" "}
      Go to this range
    </MenuItem>
  );
}
